import { Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Optional, Output, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormGroup, NgControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, filter, firstValueFrom, map, takeUntil } from 'rxjs';
import AdjuntosClass from 'src/app/model/api/datos/adjuntosClass';
import { IonicComponentsModule } from 'src/app/modules/ionic-components.module';
import { AdjuntoService } from 'src/app/services/adjunto.service';
import { AlertService } from 'src/app/services/alert.service';
import { UtilService } from 'src/app/services/util.service';
import { IonModal } from '@ionic/angular/standalone'
import { DatabaseService } from 'src/app/database/database.service';
import { LoaderService } from 'src/app/services/loader.service';
import { MainService } from 'src/app/api/main.service';

@Component({
  selector: 'app-adjuntos',
  templateUrl: './adjuntos.component.html',
  styleUrls: ['./adjuntos.component.scss'],
  standalone: true,
  imports: [
    IonicComponentsModule
  ]
})
export class AdjuntosComponent implements ControlValueAccessor {

  //#region Propiedades

  private _AdjuntosSubject: BehaviorSubject<AdjuntosClass[]>
  @Input("adjuntosSubject") public set AdjuntosSubject(value: BehaviorSubject<AdjuntosClass[]>) {
    this._AdjuntosSubject = value
    if (!value) {
      this.adjuntosFiltrados = null
      return
    }
    this.adjuntosFiltrados = this.AdjuntosSubject.pipe(
      map(p => [...p].filter(q => q.Borrado == null || q.Borrado === false))
    )
  }
  public get AdjuntosSubject(): BehaviorSubject<AdjuntosClass[]> {
    return this._AdjuntosSubject
  }
  public adjuntosFiltrados: Observable<AdjuntosClass[]>
  @Input("categoria") public Categoria: string = null
  @Input("form") public form: FormGroup<any>
  @ViewChild("mdlEditar") public mdlEditar: IonModal
  @ViewChild("iframePDF") public iframePDF: ElementRef<HTMLIFrameElement>
  private _value: string
  public get value(): string {
    return this._value
  }
  @Input("value") public set value(val: string) {
    this._value = val
  }
  @Output("valueChange") public valueChange = new EventEmitter<string>()
  @Output("valueSelected") public valueSelected = new EventEmitter<{newValue: string, oldValue: string}>()
  @Input("readonly") public readonly: boolean = false

  //#endregion

  //#region Constructor

  constructor(
    private adjuntoService: AdjuntoService,
    private alertService: AlertService,
    private zone: NgZone,
    private util: UtilService,
    private database: DatabaseService,
    private loader: LoaderService,
    private service: MainService,
    @Self() @Optional() private control: NgControl
  ) { 
    if (this.control) this.control.valueAccessor = this
  }

  //#endregion

  //#region Init

  private destroy$ = new Subject<void>()
  public async ngOnInit(): Promise<void> {
    this.AdjuntosSubject.pipe(
      takeUntil(this.destroy$),
      map(p => {
        if (this.Categoria == null) return p.filter(q => q.Categoria == null)
        return p.filter(q => q.Categoria == this.Categoria)
      })
    ).subscribe(p => {
      let oldValue = this.value
      if (p.length == 0) {
        this.value = ""
      } else {
        this.value = p.length.toString()
      }
      if (oldValue != this.value) {
        this.onChange(this.value)
        this.valueChange.emit(this.value)
        this.valueSelected.emit({
          newValue: this.value,
          oldValue: oldValue
        })
      }
    })
  }

  public async ngOnDestroy(): Promise<void> {
    this.destroy$.next()
    this.destroy$.complete()
  }

  //#endregion

  //#region Adjuntos

  public async AdjuntarArchivo(): Promise<void> {
    if (this.readonly) return
    this.adjuntoService.AdjuntarArchivo(this.form.get('LocalId')?.value, this.Categoria)
  }

  public downloadAdjunto(adjunto: AdjuntosClass) {
    let link = document.createElement('a')
    link.download = adjunto.FileName
    link.href = adjunto.AdjuntoData
    link.click()
  }

  public async EliminarAdjunto(adjunto: AdjuntosClass): Promise<void> {
    if (this.readonly) return
    if (await this.alertService.showConfirm('¿Quieres eliminar el fichero seleccionado?', 'Eliminar fichero', 'Sí, eliminar', 'No, cancelar')) {
      let adjuntos = await firstValueFrom(this.AdjuntosSubject)
      if (adjunto.Id === 0) {
        adjuntos = adjuntos.filter(p => p != adjunto)
      } else {
        adjunto.Borrado = true
      }
      this.zone.run(async() => {
        this.AdjuntosSubject.next(adjuntos)
      })
    }
  }

  public async updateAdjunto(adjunto: AdjuntosClass, base64: string): Promise<void> {
    let base64Data = base64.substring(base64.indexOf(',') + 1)
    adjunto.SHA256 = await this.util.SHA256FromBase64Data(base64Data)
    adjunto.AdjuntoData = base64
  }

  public HayAdjuntos(adjuntos: AdjuntosClass[]): boolean {
    if (this.Categoria == null) {
      return adjuntos != null && adjuntos.filter(p => p.Categoria == null).length > 0
    } else {
      return adjuntos != null && adjuntos.filter(p => p.Categoria == this.Categoria).length > 0
    }
  }

  //#endregion

  //#region Modal Edición PDF

  public esPDF(adjunto: AdjuntosClass): boolean {
    return adjunto.FileName.toLowerCase().endsWith('.pdf')
  }

  public async editarPDF(adjunto: AdjuntosClass): Promise<void> {
    this.mdlEditar.isOpen = true

    // Guardamos el adjunto en IndexedDB para poder editarlo
    let localId = await this.database.Adjuntos.update(adjunto)
    adjunto.LocalId = localId

    await this.util.Sleep(1)

    // Cargamos el fichero
    let adjuntoId = adjunto.LocalId
    sessionStorage.setItem('idbattach', adjuntoId.toString())
    let url = `/assets/pdfviewer/viewer.html`
    this.iframePDF.nativeElement.src = url
  }

  public async guardarPDF(adjunto: AdjuntosClass): Promise<void> {
    try {
      let pdfData = await this.iframePDF.nativeElement.contentWindow["PDFViewerApplication"].getDataURL()
      
      // Cargamos los adjuntos del subject
      let adjuntos = await firstValueFrom(this.AdjuntosSubject)
      let adjuntoEdicion = adjuntos.find(p => p.LocalId == adjunto.LocalId)
      adjuntoEdicion.AdjuntoData = pdfData
      await this.database.Adjuntos.update(adjuntoEdicion)
      this.AdjuntosSubject.next(adjuntos)

      this.mdlEditar.isOpen = false

    } catch (ex) {
      console.error(ex)
      await this.alertService.showMessage(`No se ha podido guardar el documento.`)
    }
  }

  //#endregion

  //#region Reactive Forms

  private onChange = (value: string) => {}
  private onTouched = () => {}

  writeValue(value: string): void {
    this.value = value
    this.valueChange.emit(value)
    this.onChange(value)
  }

  registerOnChange(fn: (value: string) => {}): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn
  }

  public isInvalid(): boolean {
    return this.control ? this.control.invalid : false
  }

  public isTouched(): boolean {
    return this.control ? this.control.touched : false
  }

  //#endregion

  //#region Sincronización de adjuntos

  public async sincronizarAdjuntos(): Promise<void> {
    try {
      await this.loader.show('Sincronizando adjuntos...')
      let adjuntosLocales = await firstValueFrom(this.AdjuntosSubject)
      let adjuntosInspeccion = await this.service.Inspecciones.getFicheros(this.form.get('Id')?.value)
      let adjuntosNuevos = adjuntosInspeccion.filter(p => adjuntosLocales.find(q => q.Id == p.Id) == null)
      for (let adjuntoNuevo of adjuntosNuevos) {
        await this.loader.hide()
        await this.loader.show(`Descargando adjunto ${adjuntosNuevos.indexOf(adjuntoNuevo) + 1} de ${adjuntosNuevos.length}`)
        let adjuntoLocal = new AdjuntosClass()
        adjuntoLocal.Id = adjuntoNuevo.Id
        adjuntoLocal.FileName = adjuntoNuevo.FileName
        adjuntoLocal.OrdenLocalId = this.form.get('LocalId')?.value
        adjuntoLocal.SHA256 = adjuntoNuevo.SHA256
        adjuntoLocal.Categoria = adjuntoNuevo.Categoria != null && adjuntoNuevo.Categoria != '' ? adjuntoNuevo.Categoria : null
        adjuntoLocal.AdjuntoData = await this.service.Inspecciones.downloadFichero(this.form.get('Id')?.value, adjuntoNuevo.Id)
        adjuntosLocales.push(adjuntoLocal)

        await this.database.Adjuntos.update(adjuntoLocal)
      }
      this.AdjuntosSubject.next(adjuntosLocales)
      await this.loader.hide()
      await this.loader.show('Actualizando...')
      await this.loader.hide()
    } catch (ex) {
      await this.loader.hideAll()
      await this.alertService.showMessage(`No se ha podido sincronizar los adjuntos: ${ex?.message || 'Error desconocido.'}`)
    }
  }

  //#endregion

}
